const { types } = require('@showrunner/codex')

const SCRATCH_TITLE_PAGE = [
  'Script Title',
  'Written by',
  'Name of First Writer',
  'Based on, If Any',
  'Address',
  'Phone Number'
]

function getProseMarks (block) {
  if (!block.attributes.Style) return false

  let isCaps = false
  const marks = block.attributes.Style.split('+')
    // turn 'Bold+Italic' > [{ type: 'em' }, { type: 'strong' }]
    .map(m => {
      if (m === 'Bold') return { type: types.STRONG }
      if (m === 'Italic') return { type: types.EM }
      if (m === 'Underline') return { type: types.UNDERLINE }
      if (m === 'Strikeout') return { type: types.STRIKE }
      // in PM we dont use a mark for AllCaps style tags like FD. we format the text itself
      if (m === 'AllCaps') {
        isCaps = true
        return null
      }
      console.warn(`Unknown mark type: "${m}". Skipping.`)
      return null
    })
    .filter(x => x) // filter out null values

  return { marks: marks.length > 0 ? marks : null, isCaps }
}

function createDualNode () {
  return {
    type: types.DUAL_DIALOGUE,
    content: [{
      type: types.DUAL_DIALOGUE_COLUMN,
      content: []
    }, {
      type: types.DUAL_DIALOGUE_COLUMN,
      content: []
    }]
  }
}

function parseTitlePage (titlePage) {
  // we needn't pass through canned/default FD content
  return titlePage.filter(block =>
    block.children[0].text &&
      !SCRATCH_TITLE_PAGE.includes(block.children[0].text[0])
  )
    .map(block => {
      return {
        type: types.ACTION,
        content: [
          {
            text: block.children[0].text[0],
            type: types.TEXT
          }
        ]
      }
    })
}

/**
 * Get a valid Prose Script node type from a FDX node type.
 * If type is unknown, defaults to action.
 * @param {string} fdxType - FDX node type name
 * @returns {string} prose node type name
 */
function getType (fdxType) {
  // consolidates End Of Act, End of Act
  switch (fdxType.toLowerCase()) {
    case 'scene heading': return types.SCENE_HEADING
    case 'character': return types.CHARACTER
    case 'dialogue': return types.DIALOGUE
    case 'parenthetical': return types.PARENTHETICAL
    case 'transition': return types.TRANSITION
    case 'action': return types.ACTION
    case 'general': return types.GENERAL
    case 'new act': return types.NEW_ACT
    case 'end of act': return types.END_OF_ACT
    case 'cold opening': return types.NEW_ACT
  }
  console.warn(`Unknown node type: "${fdxType}". Defaulting to ${types.GENERAL}.`)
  return types.GENERAL
}

/**
 * Convert a parsed array of objects representing a FDX document into a valid
 * ProseMirror JSON object based on codex's proseScript schema.
 * @param {array} scriptNodes - ordered list of nodes parsed from FDX script
 * @param {array} titlePageNodes - ordered list of nodes from FDX title page
 * @param {function} callback - (err, proseDoc)
 */
function fdxToProse (scriptNodes, titlePageNodes, callback) {
  // create a scratch document
  const doc = { type: types.DOC, content: [] }
  const titleContent = parseTitlePage(titlePageNodes)

  let currentPageIndex = 0

  if (titleContent.length > 0) {
    doc.content.push({
      type: types.PAGE,
      attrs: { dynamic: false },
      content: titleContent
    })
    currentPageIndex++
  }

  // no need to increment our counter for the first page
  doc.content.push({
    type: types.PAGE,
    attrs: { dynamic: false },
    content: []
  })

  let dualNode = null
  let dualColumnIndex = 0

  function addToDualNode (el) {
    dualNode.content[dualColumnIndex].content.push(el)
  }

  // filter out scriptNotes
  scriptNodes.filter(node => node.type)
    .forEach((node, nodeIndex, nodes) => {
      const positionToSplice = []
      const content = node.children
        .filter(block => block.text) // ignore empty tags that dont actually contain text
        .map(block => {
          if (block.startsNewPage && doc.content[currentPageIndex].content.length > 0) {
            // start a new page if the block requires it (unless the previous page is empty)
            doc.content.push({
              type: types.PAGE,
              attrs: { dynamic: false },
              content: []
            })
            currentPageIndex++
          }

          const proseBlock = {
            type: types.TEXT,
            text: block.text[0] // we havent seen more than one in the wild yet
          }

          const { marks, isCaps } = getProseMarks(block)

          if (isCaps) proseBlock.text = proseBlock.text.toUpperCase()
          if (marks) proseBlock.marks = marks

          return proseBlock
        })
        .map((block, idx, arr) => {
          // if neither this block nor the previous one are decorated, concatenate the strings
          if (idx > 0 && !block.marks && !arr[idx - 1].marks) {
            positionToSplice.push(idx - 1)
            block.text = arr[idx - 1].text + block.text
          }
          return block
        })
        // remove blocks that were concatenated above
        .filter((block, idx) => !positionToSplice.includes(idx))

      const type = getType(node.type)

      // normal node
      if (!node.dual) {
        // add current content to doc
        return doc.content[currentPageIndex].content.push({
          type,
          content
        })
      }

      // we're in a dual dialogue block

      // this is the beginning of a dual dialogue column
      if (type === types.CHARACTER) {
        // this is the first column
        if (node.dualMarker === true) {
          dualColumnIndex = 1
        } else {
          dualNode = createDualNode()
          dualColumnIndex = 0
        }
      }
      // handle dual dialogue column content
      addToDualNode({ type, content })

      const nextNode = nodes[nodeIndex + 1]
      const isLast = nextNode == null
      const nextIsNotDual = !isLast && !nextNode.dual
      const nextIsNewDual = !isLast &&
        nextNode.dual === true &&
        getType(nextNode.type) === types.CHARACTER &&
        dualColumnIndex === 1

      // add dual dialogue node to document under any of these conditions:
      // - current node is last script node
      // - next node is not a dual dialogue node
      // - next node is beginning of a new dual dialogue node
      if (isLast || nextIsNotDual || nextIsNewDual) {
        doc.content[currentPageIndex].content.push(dualNode)
      }
    })

  return callback(null, doc)
}

module.exports = { fdxToProse, SCRATCH_TITLE_PAGE }
